import React, { useEffect, useState } from 'react';
import { CalendarDays, Clock3, IndianRupee, Phone } from 'lucide-react';
import { useTenant } from '../../../../context/TenantContext';
import { CURRENCIES, DATE_FORMATS, formatDisplayDate, PHONE_FORMATS, TIME_FORMATS } from '../../../../lib/displayFormat';
import { apiError } from '../../../../lib/host';
import { LANGUAGES, loadLanguage, translate, useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { ErrorBanner, useBasic } from '../../ui';
import { Card, ChoiceCards, Field, SectionHead } from '../components/SettingsUI';
import TimezoneSelect from '../components/TimezoneSelect';

const FLAGS = { en: '🇬🇧', ta: 'த', hi: 'हि', te: 'తె', kn: 'ಕ' };
const FIRST_DAYS = [['monday', 'Monday'], ['sunday', 'Sunday'], ['saturday', 'Saturday']];

/** A few lines of the dashboard in the chosen language. */
function LanguagePreview({ lang, name }) {
  const tr = (text, vars) => translate(lang, text, vars);
  const cards = [['Total Calls', '428', '#16a34a'], ['New Leads', '87', '#2563eb'], ['Appointments', '24', '#f59e0b']];
  return (
    <div className="st-lang-preview" lang={lang}>
      <div className="st-lang-side">
        <b>TeleCalX</b>
        {['Dashboard', 'Phone Numbers', 'Calls & Conversations', 'Contacts', 'Settings'].map((n, i) => <span key={n} className={i === 0 ? 'on' : ''}>{tr(n)}</span>)}
      </div>
      <div className="st-lang-main">
        <h4>{tr('Good morning')}, {name}! 👋</h4>
        <p>{tr("Here's what's happening with your business today.")}</p>
        <div className="st-lang-cards">
          {cards.map(([label, value, color]) => <span key={label}><i style={{ background: color }} /><small>{tr(label)}</small><b>{value}</b></span>)}
        </div>
      </div>
    </div>
  );
}

/** Settings › Language: the dashboard's language, and how dates, times, phone numbers and money are shown. */
export default function LanguageSection() {
  const { t } = useT();
  const { tenant } = useTenant();
  const { notify, person } = useBasic();
  const { prefs, save } = usePreferences();
  const [form, setForm] = useState(prefs.language);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = JSON.stringify(form) !== JSON.stringify(prefs.language);
  const [, setLoaded] = useState(null); // re-render the preview once the chosen language's file arrives
  useEffect(() => { loadLanguage(form.language).then(() => setLoaded(form.language)); }, [form.language]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value || null : e });

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await save('language', form);
      notify(translate(form.language, 'Language and formats saved.'));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const companyDefault = (label) => t('Company default ({value})', { value: label });
  return (
    <>
      <SectionHead title={t('Language')} subtitle={t('Choose your preferred language for the dashboard and communications.')}>
        <button className="primary-action" disabled={busy || !dirty} onClick={submit}>{busy ? t('Saving…') : t('Save Changes')}</button>
      </SectionHead>
      <ErrorBanner message={error} />
      <Card title={t('Application Language')} subtitle={t('Select the language for the dashboard interface, menus and messages.')}>
        <ChoiceCards name={t('Application Language')} layout="column" columns={5} value={form.language} onChange={set('language')}
          options={LANGUAGES.map((l) => ({ value: l.code, glyph: <span className={`st-flag lang-${l.code}`}>{FLAGS[l.code]}</span>, label: l.native, sub: l.code === 'en' ? t('Default') : l.name }))} />
        <small className="st-hint st-pad">{t('Menus, Settings and Help & Support appear in this language; other pages are being translated.')}</small>
      </Card>

      <Card title={t('Date & Time Format')} subtitle={t('Choose how dates and times are displayed across the application.')}>
        <div className="st-grid">
          <Field label={t('Date Format')} icon={CalendarDays}>
            <select value={form.date_format || ''} onChange={set('date_format')}>
              <option value="">{companyDefault(tenant.date_format || 'DD MMM YYYY')}</option>
              {DATE_FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label={t('Time Format')} icon={Clock3}>
            <select value={form.time_format || ''} onChange={set('time_format')}>
              <option value="">{companyDefault(tenant.time_format === '24' ? t('24 Hour (10:30)') : t('12 Hour (10:30 AM)'))}</option>
              {TIME_FORMATS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </select>
          </Field>
          <Field label={t('First Day of Week')} icon={CalendarDays}>
            <select value={form.first_day} onChange={set('first_day')}>{FIRST_DAYS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
          </Field>
          <Field label={t('Timezone')} icon={Clock3}>
            <TimezoneSelect value={form.timezone} onChange={set('timezone')} empty={companyDefault(tenant.timezone || 'Asia/Kolkata')} />
          </Field>
        </div>
        <small className="st-hint st-pad">{t('Today will show as {date}.', { date: formatDisplayDate(new Date(), form.date_format || tenant.date_format || 'DD MMM YYYY') })}</small>
      </Card>

      <Card title={t('Number & Currency Format')} subtitle={t('Set the preferred format for phone numbers and currency values.')}>
        <div className="st-grid">
          <Field label={t('Phone Number Format')} icon={Phone}>
            <select value={form.phone_format} onChange={set('phone_format')}>{PHONE_FORMATS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
          </Field>
          <Field label={t('Currency Format')} icon={IndianRupee}>
            <select value={form.currency} onChange={set('currency')}>{CURRENCIES.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
          </Field>
        </div>
        <small className="st-hint st-pad">{t('Invoices and plan prices stay in Indian Rupees (INR), the currency TeleCalX bills in.')}</small>
      </Card>

      <Card title={t('Language Preview')} subtitle={t('See how the dashboard will look with the selected language.')}>
        <LanguagePreview lang={form.language} name={(person || '').split(' ')[0]} />
      </Card>
    </>
  );
}
